import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/ThemeProvider";
import { TenantProvider } from "@/hooks/useTenant";
import { ErrorBoundary, PageLoader } from "@/components/ErrorBoundary";
import { AdminGuard } from "@/components/AdminGuard";
import { lazy, Suspense, useEffect } from "react";
import { useAuthRoutingState } from "@/hooks/useAuthRoutingState";
import { RouteVisitTracker, markInternalTraffic } from "@/hooks/useAnalyticsTracking";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { IdentityLinker } from "@/components/IdentityLinker";

function InternalTrafficFlag() {
  const { isAdmin } = useIsAdmin();
  useEffect(() => {
    if (isAdmin) markInternalTraffic(true);
  }, [isAdmin]);
  return null;
}

// Eager: auth-critical paths
import Auth from "./pages/Auth";
import Onboarding from "./pages/Onboarding";

// Lazy: all other pages
const Index = lazy(() => import("./pages/Index"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const Prospects = lazy(() => import("./pages/Prospects"));
const ContactDetail = lazy(() => import("./pages/ContactDetail"));
const Conversations = lazy(() => import("./pages/Conversations"));
const Pipeline = lazy(() => import("./pages/Pipeline"));
const Analytics = lazy(() => import("./pages/Analytics"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const Inbox = lazy(() => import("./pages/Inbox"));
const Train = lazy(() => import("./pages/Train"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Pricing = lazy(() => import("./pages/Pricing"));
const VideoPage = lazy(() => import("./pages/VideoPage"));
const BookPage = lazy(() => import("./pages/BookPage"));
const BestAISDRTools = lazy(() => import("./pages/BestAISDRTools"));
const WhatIsAISDR = lazy(() => import("./pages/WhatIsAISDR"));
const HowAISDRsWork = lazy(() => import("./pages/HowAISDRsWork"));
const SmartleadAlternatives = lazy(() => import("./pages/SmartleadAlternatives"));
const AutoPitchVsSmartlead = lazy(() => import("./pages/AutoPitchVsSmartlead"));
const AutoPitchVsApollo = lazy(() => import("./pages/AutoPitchVsApollo"));
const BookMoreMeetingsWithAI = lazy(() => import("./pages/BookMoreMeetingsWithAI"));
const AISDRROICalculator = lazy(() => import("./pages/AISDRROICalculator"));
const SuperBrain = lazy(() => import("./pages/SuperBrain"));
const Approvals = lazy(() => import("./pages/Approvals"));
const Verticals = lazy(() => import("./pages/Verticals"));
const SendingHealth = lazy(() => import("./pages/SendingHealth"));
const AISDRPricing = lazy(() => import("./pages/AISDRPricing"));
const AutomateColdOutreach = lazy(() => import("./pages/AutomateColdOutreach"));
const Blog = lazy(() => import("./pages/Blog"));
const Videos = lazy(() => import("./pages/Videos"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));
const ProgrammaticPage = lazy(() => import("./pages/ProgrammaticPage"));
const LandingES = lazy(() => import("./pages/LandingES"));
const LandingPTBR = lazy(() => import("./pages/LandingPTBR"));
const LandingDE = lazy(() => import("./pages/LandingDE"));
const CalendarCallback = lazy(() => import("./pages/auth/CalendarCallback"));
const GraphCallback = lazy(() => import("./pages/auth/GraphCallback"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const ConsentCaseStudy = lazy(() => import("./pages/ConsentCaseStudy"));
const Library = lazy(() => import("./pages/Library"));
const CampaignBuilder = lazy(() => import("./pages/CampaignBuilder"));

// Admin pages
const AdminOverview = lazy(() => import("./pages/admin/Overview"));
const AdminTenants = lazy(() => import("./pages/admin/Tenants"));
const AdminHealth = lazy(() => import("./pages/admin/Health"));
const AdminBilling = lazy(() => import("./pages/admin/Billing"));
const AdminCampaigns = lazy(() => import("./pages/admin/Campaigns"));
const AdminAnalytics = lazy(() => import("./pages/admin/Analytics"));
const AdminConversations = lazy(() => import("./pages/admin/Conversations"));
const AdminPipeline = lazy(() => import("./pages/admin/Pipeline"));
const AdminSettings = lazy(() => import("./pages/admin/Settings"));
const AdminCostTracker = lazy(() => import("./pages/admin/CostTracker"));
const AdminGrowth = lazy(() => import("./pages/admin/Growth"));
const AdminAuditLog = lazy(() => import("./pages/admin/AuditLog"));
const AdminSendCapacityAudit = lazy(() => import("./pages/admin/SendCapacityAudit"));
const AdminSendCapacity = lazy(() => import("./pages/admin/SendCapacity"));
const AdminEmailEvaluation = lazy(() => import("./pages/admin/EmailEvaluation"));
const AdminBetaPromo = lazy(() => import("./pages/admin/BetaPromo"));
const AdminFeedback = lazy(() => import("./pages/admin/Feedback"));
const AdminCaseStudies = lazy(() => import("./pages/admin/CaseStudies"));
const AdminVerticals = lazy(() => import("./pages/admin/Verticals"));
const AdminWhiteGlove = lazy(() => import("./pages/admin/WhiteGlove"));
const SignalDiscoveryPreview = lazy(() => import("./pages/SignalDiscoveryPreview"));
const Meetings = lazy(() => import("./pages/Meetings"));
const LegalDPA = lazy(() => import("./pages/legal/DPA"));
const LegalSubprocessors = lazy(() => import("./pages/legal/Subprocessors"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
  },
});

function ActivationRecoveryCard({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="onboarding-shell min-h-screen flex items-center justify-center bg-background px-4">
      <div className="max-w-md w-full rounded-xl glass-surface p-8 text-center space-y-5 relative z-10">
        <div className="h-12 w-12 rounded-xl bg-destructive/10 flex items-center justify-center mx-auto">
          <span className="text-2xl">⚠️</span>
        </div>
        <div className="space-y-2">
          <h2 className="text-lg font-semibold text-foreground">Workspace setup didn't finish</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Your account was created, but we couldn't finish provisioning your workspace.
            This is usually a one-time hiccup — try again, or contact support if it persists.
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <button
            onClick={onRetry}
            className="w-full h-10 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            Retry setup
          </button>
          <a
            href="mailto:bryan@autopitch.io"
            className="w-full h-10 rounded-md border border-border text-sm flex items-center justify-center hover:bg-muted/50 transition-colors"
          >
            Contact support
          </a>
        </div>
      </div>
    </div>
  );
}

function AppRoutes() {
  const { session, loading, hasTenant, onboardingCompleted, repairFailed, retryRepair } = useAuthRoutingState();

  if (loading) return <PageLoader />;

  if (!session) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Auth />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/auth/calendar/callback" element={<CalendarCallback />} />
          <Route path="/auth/graph/callback" element={<GraphCallback />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/book/:slug" element={<BookPage />} />
          <Route path="/best-ai-sdr-tools" element={<BestAISDRTools />} />
          <Route path="/what-is-ai-sdr" element={<WhatIsAISDR />} />
          <Route path="/how-ai-sdrs-work" element={<HowAISDRsWork />} />
          <Route path="/smartlead-alternatives" element={<SmartleadAlternatives />} />
          <Route path="/autopitch-vs-smartlead" element={<AutoPitchVsSmartlead />} />
          <Route path="/autopitch-vs-apollo" element={<AutoPitchVsApollo />} />
          <Route path="/book-more-meetings-with-ai" element={<BookMoreMeetingsWithAI />} />
          <Route path="/ai-sdr-roi-calculator" element={<AISDRROICalculator />} />
          <Route path="/ai-sdr-pricing" element={<AISDRPricing />} />
          <Route path="/automate-cold-outreach" element={<AutomateColdOutreach />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/videos" element={<Videos />} />
          <Route path="/blog/:slug" element={<BlogArticle />} />
          <Route path="/solutions/:slug" element={<ProgrammaticPage />} />
          <Route path="/v/:slug" element={<VideoPage />} />
          <Route path="/es" element={<LandingES />} />
          <Route path="/pt-br" element={<LandingPTBR />} />
          <Route path="/de" element={<LandingDE />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/consent/case-study" element={<ConsentCaseStudy />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    );
  }

  if (hasTenant === null) return <PageLoader />;

  // Self-healing repair exhausted — surface a recovery card instead of looping
  if (hasTenant === false && repairFailed) {
    return <ActivationRecoveryCard onRetry={retryRepair} />;
  }

  // User has tenant but hasn't completed onboarding
  if (!onboardingCompleted) {
    return (
      <Routes>
        <Route path="*" element={<Onboarding />} />
      </Routes>
    );
  }

  return (
    <TenantProvider>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Tenant-facing routes */}
          <Route path="/" element={<Index />} />
          <Route path="/inbox" element={<Inbox />} />
          <Route path="/train" element={<Train />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/prospects" element={<Prospects />} />
          <Route path="/contacts/:id" element={<ContactDetail />} />
          <Route path="/conversations" element={<Conversations />} />
          <Route path="/pipeline" element={<Pipeline />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/sending-health" element={<SendingHealth />} />
          <Route path="/signal-discovery-preview" element={<SignalDiscoveryPreview />} />
          <Route path="/meetings" element={<Meetings />} />
          <Route path="/admin/super-brain" element={<AdminGuard><SuperBrain /></AdminGuard>} />
          {/* Legacy redirect — Super Brain was briefly a top-level surface. */}
          <Route path="/super-brain" element={<Navigate to="/admin/super-brain" replace />} />

          {/* Approvals — operator inbox for Super Brain escalations */}
          <Route path="/approvals" element={<Approvals />} />
          <Route path="/verticals" element={<Verticals />} />
          <Route path="/library" element={<Library />} />
          {/* (legacy /meetings → pipeline removed; /meetings is now the real Meetings page) */}
          <Route path="/companies" element={<Navigate to="/prospects" replace />} />
          <Route path="/organizations" element={<Navigate to="/prospects" replace />} />
          <Route path="/campaigns" element={<Navigate to="/campaigns/builder" replace />} />
          <Route path="/campaigns/builder" element={<CampaignBuilder />} />
          <Route path="/pitch" element={<Navigate to="/train?tab=messaging" replace />} />
          <Route path="/icp-agent" element={<Navigate to="/train?tab=target" replace />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/auth/calendar/callback" element={<CalendarCallback />} />
          <Route path="/auth/graph/callback" element={<GraphCallback />} />
          <Route path="/book/:slug" element={<BookPage />} />
          <Route path="/best-ai-sdr-tools" element={<BestAISDRTools />} />
          <Route path="/what-is-ai-sdr" element={<WhatIsAISDR />} />
          <Route path="/how-ai-sdrs-work" element={<HowAISDRsWork />} />
          <Route path="/smartlead-alternatives" element={<SmartleadAlternatives />} />
          <Route path="/autopitch-vs-smartlead" element={<AutoPitchVsSmartlead />} />
          <Route path="/autopitch-vs-apollo" element={<AutoPitchVsApollo />} />
          <Route path="/book-more-meetings-with-ai" element={<BookMoreMeetingsWithAI />} />
          <Route path="/ai-sdr-roi-calculator" element={<AISDRROICalculator />} />
          <Route path="/ai-sdr-pricing" element={<AISDRPricing />} />
          <Route path="/automate-cold-outreach" element={<AutomateColdOutreach />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/videos" element={<Videos />} />
          <Route path="/blog/:slug" element={<BlogArticle />} />
          <Route path="/solutions/:slug" element={<ProgrammaticPage />} />
          <Route path="/v/:slug" element={<VideoPage />} />
          <Route path="/es" element={<LandingES />} />
          <Route path="/pt-br" element={<LandingPTBR />} />
          <Route path="/de" element={<LandingDE />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />

          {/* Admin routes — protected by role check */}
          <Route path="/admin" element={<AdminGuard><AdminOverview /></AdminGuard>} />
          <Route path="/admin/tenants" element={<AdminGuard><AdminTenants /></AdminGuard>} />
          <Route path="/admin/campaigns" element={<AdminGuard><AdminCampaigns /></AdminGuard>} />
          <Route path="/admin/conversations" element={<AdminGuard><AdminConversations /></AdminGuard>} />
          <Route path="/admin/pipeline" element={<AdminGuard><AdminPipeline /></AdminGuard>} />
          <Route path="/admin/analytics" element={<AdminGuard><AdminAnalytics /></AdminGuard>} />
          <Route path="/admin/health" element={<AdminGuard><AdminHealth /></AdminGuard>} />
          <Route path="/admin/billing" element={<AdminGuard><AdminBilling /></AdminGuard>} />
          <Route path="/admin/costs" element={<AdminGuard><AdminCostTracker /></AdminGuard>} />
          <Route path="/admin/growth" element={<AdminGuard><AdminGrowth /></AdminGuard>} />
          <Route path="/admin/settings" element={<AdminGuard><AdminSettings /></AdminGuard>} />
          <Route path="/admin/audit" element={<AdminGuard><AdminAuditLog /></AdminGuard>} />
          <Route path="/admin/send-capacity" element={<AdminGuard><AdminSendCapacity /></AdminGuard>} />
          <Route path="/admin/send-capacity-audit" element={<Navigate to="/admin/email-evaluation" replace />} />
          <Route path="/admin/email-evaluation" element={<AdminGuard><AdminEmailEvaluation /></AdminGuard>} />
          <Route path="/admin/beta-promo" element={<AdminGuard><AdminBetaPromo /></AdminGuard>} />
          <Route path="/admin/feedback" element={<AdminGuard><AdminFeedback /></AdminGuard>} />
          <Route path="/admin/case-studies" element={<AdminGuard><AdminCaseStudies /></AdminGuard>} />
          <Route path="/admin/verticals" element={<AdminGuard><AdminVerticals /></AdminGuard>} />
          <Route path="/admin/whiteglove" element={<AdminGuard><AdminWhiteGlove /></AdminGuard>} />

          <Route path="/legal/dpa" element={<LegalDPA />} />
          <Route path="/legal/subprocessors" element={<LegalSubprocessors />} />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </TenantProvider>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ErrorBoundary>
            <RouteVisitTracker />
            <InternalTrafficFlag />
            <IdentityLinker />
            <AppRoutes />
          </ErrorBoundary>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
